import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Database, LayoutGrid, FileText, Users, Building2, Landmark, ShieldAlert, Loader2, BadgeCheck, Wand2, UserCog, Award, Trash2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import DocumentsTab from "@/components/library/DocumentsTab";
import PeopleTab from "@/components/library/PeopleTab";
import CompaniesTab from "@/components/library/CompaniesTab";
import AgenciesTab from "@/components/library/AgenciesTab";
import LearnedMapsTab from "@/components/library/LearnedMapsTab";
import { KeyPersonnelPanel, PastPerformancePanel } from "@/components/library/BrainPanels";
import CompanyFile from "@/pages/CompanyFile";

const GOLD = "#c8a24b";
const PP_COLORS = { Compliant: "#16a34a", Limited: "#d97706", Incomplete: "#dc2626" };
const POOL_COLORS = ["#c8a24b", "#71717a"];

const TABS = [
  ["dashboard", LayoutGrid, "Dashboard"],
  ["company", BadgeCheck, "Company Profile"],
  ["documents", FileText, "Documents"],
  ["people", Users, "People"],
  ["companies", Building2, "Companies"],
  ["agencies", Landmark, "Agencies"],
  ["personnel", UserCog, "Key Personnel"],
  ["performance", Award, "Past Performance"],
  ["learned", Wand2, "Learned Maps"],
];

export default function DataLibrary() {
  const [params] = useSearchParams();
  const initial = params.get("tab") || "dashboard";
  const [tab, setTab] = useState(TABS.some(([k]) => k === initial) ? initial : "dashboard");
  const [counts, setCounts] = useState({});
  const [showPurge, setShowPurge] = useState(false);
  const { user } = useAuth();
  const canPurge = user?.role === "admin" || user?.role === "officer";
  const loadCounts = () => api.get("/library/counts").then((r) => setCounts(r.data)).catch(() => {});
  useEffect(() => { loadCounts(); }, []);

  return (
    <div className="p-5 sm:p-8 max-w-[1600px] mx-auto" data-testid="data-library-page">
      {/* PAGE-LEVEL CUI ADVISORY (not a definitive marking) */}
      <div data-testid="cui-banner"
        className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] font-semibold tracking-wide text-amber-800 mb-5">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        This page may contain Controlled Unclassified Information (CUI). Handle per each item’s marking.
      </div>

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900 flex items-center gap-2.5">
            <Database className="h-7 w-7" /> Data Library
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            The company knowledge brain. Everything finalized here feeds Anvil in real time.
          </p>
        </div>
        {canPurge && (
          <button data-testid="clear-library-btn" onClick={() => setShowPurge(true)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border bg-white text-red-600 border-red-200 hover:border-red-400 shrink-0">
            <Trash2 className="h-4 w-4" /> Clear test/seed data
          </button>
        )}
      </div>
      {showPurge && <LibraryPurgeModal onClose={() => setShowPurge(false)} onDone={() => { setShowPurge(false); loadCounts(); }} />}

      <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto" data-testid="library-tabs">
        {TABS.map(([k, Icon, label]) => (
          <button key={k} data-testid={`library-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === k ? "border-gold-dark text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>
            <Icon className="h-4 w-4" /> {label}
            {k !== "dashboard" && counts[k] != null && (
              <span className="ml-1 text-[10px] bg-zinc-100 text-zinc-600 rounded-full px-1.5 py-0.5">{counts[k]}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "dashboard" && <Dashboard />}
        {tab === "company" && <div data-testid="company-profile-tab"><CompanyFile embedded /></div>}
        {tab === "documents" && <DocumentsTab onChange={loadCounts} />}
        {tab === "people" && <PeopleTab />}
        {tab === "companies" && <CompaniesTab />}
        {tab === "agencies" && <AgenciesTab onChange={loadCounts} />}
        {tab === "personnel" && <div data-testid="personnel-tab"><KeyPersonnelPanel /></div>}
        {tab === "performance" && <div data-testid="performance-tab"><PastPerformancePanel /></div>}
        {tab === "learned" && <LearnedMapsTab />}
      </div>
    </div>
  );
}

const CAT_LABELS = {
  key_personnel: "Key Personnel (Brain)", past_performance: "Past Performance (Brain)",
  kb_documents: "KB Documents", companies: "Companies", contacts: "Contacts",
  agencies: "Agencies", contracts: "Contracts",
};

function LibraryPurgeModal({ onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [sel, setSel] = useState({});
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/library/purge-preview").then((r) => setPreview(r.data)).catch(() => setPreview({})); }, []);
  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/library/purge", { confirm, categories: chosen });
      const total = Object.values(r.data?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Cleared ${total} item(s) across ${chosen.length} categor${chosen.length === 1 ? "y" : "ies"}.`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Purge failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-lg bg-white border border-red-200 rounded-xl p-6 max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="library-purge-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-xl text-red-700 flex items-center gap-2"><Trash2 className="h-5 w-5" /> Clear library data</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <p className="text-sm text-zinc-600 mb-3">Check each category to clear. Nothing is selected by default. The library structure and the canonical WD identity are always preserved.</p>
        {!preview ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading counts…</div>
        ) : (
          <div className="space-y-2">
            {Object.keys(CAT_LABELS).map((k) => {
              const info = preview[k] || { count: 0, sample: [] };
              return (
                <label key={k} data-testid={`purge-cat-${k}`}
                  className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${sel[k] ? "border-red-300 bg-red-50" : "border-zinc-200"}`}>
                  <input type="checkbox" checked={!!sel[k]} disabled={!info.count}
                    onChange={(e) => setSel({ ...sel, [k]: e.target.checked })} className="mt-1" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-800">{CAT_LABELS[k]} <span className="text-zinc-400 font-normal">· {info.count}</span></div>
                    {info.sample?.length > 0 && <div className="text-[11px] text-zinc-500 truncate">{info.sample.join(", ")}</div>}
                  </div>
                </label>
              );
            })}
          </div>
        )}
        <p className="text-[12px] text-zinc-500 mt-4 mb-2">Type <span className="font-mono font-bold text-red-700">CLEAR LIBRARY</span> to confirm.</p>
        <input data-testid="library-purge-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder="CLEAR LIBRARY"
          className="w-full bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-red-400 outline-none" />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-md border border-zinc-300 text-zinc-600 text-sm">Cancel</button>
          <button data-testid="library-purge-confirm-btn" disabled={busy || confirm !== "CLEAR LIBRARY" || chosen.length === 0} onClick={run}
            className="flex-1 py-2.5 rounded-md bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-40">
            {busy ? "Clearing…" : `Clear ${chosen.length || ""} selected`}
          </button>
        </div>
      </div>
    </div>
  );
}


const Card = ({ title, children, testid }) => (
  <div className="bg-white border border-zinc-200 shadow-sm rounded-xl" data-testid={testid}>
    <div className="px-4 py-3 border-b border-zinc-200">
      <span className="text-[11px] tracking-[0.1em] font-semibold text-zinc-500 uppercase">{title}</span>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Stat = ({ label, value, sub, testid }) => (
  <div className="bg-white border border-zinc-200 shadow-sm rounded-xl p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="text-3xl font-display font-extrabold text-zinc-900 mt-1">{value}</div>
    {sub && <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/library/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="flex items-center gap-2 text-sm text-zinc-500 py-10"><Loader2 className="h-4 w-4 animate-spin" /> Loading library stats…</div>;

  const people = d.people || {};
  const poolData = [{ name: "In staffing pool", value: people.in_pool || 0 }, { name: "Not in pool", value: people.not_in_pool || 0 }];
  const resumeData = [{ name: "Resume on file", value: people.with_resume || 0 }, { name: "No resume", value: people.without_resume || 0 }];
  const byType = (d.documents?.by_type || []).filter((t) => t.count > 0);
  const pp = (d.past_performance || []).filter((p) => p.count > 0);
  const ppTotal = pp.reduce((s, p) => s + p.count, 0);

  return (
    <div className="space-y-6" data-testid="library-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="stat-team" label="Our Team" value={people.total || 0} sub={`${people.in_pool || 0} in staffing pool`} />
        <Stat testid="stat-documents" label="Documents" value={d.documents?.total || 0} sub="knowledge & contract files" />
        <Stat testid="stat-companies" label="Companies" value={d.companies?.total || 0} sub={`${d.companies?.partners || 0} partners`} />
        <Stat testid="stat-agencies" label="Agencies" value={d.agencies?.total || 0} sub={`${d.agencies?.with_awards || 0} with awards`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Staffing Pool" testid="card-staffing-pool">
          <div className="grid grid-cols-2 gap-4">
            <MiniPie data={poolData} colors={POOL_COLORS} center={`${people.in_pool_pct || 0}%`} label="in pool" />
            <MiniPie data={resumeData} colors={["#16a34a", "#71717a"]} center={`${people.resume_pct || 0}%`} label="resumes" />
          </div>
        </Card>

        <Card title="Past Performance Health" testid="card-pp-health">
          {ppTotal === 0 ? <Empty msg="No past-performance summaries tagged yet." /> : (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pp} dataKey="count" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {pp.map((p) => <Cell key={p.name} fill={PP_COLORS[p.name] || "#a1a1aa"} />)}
                </Pie>
                <Tooltip /><Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card title="Document Breakdown" testid="card-doc-breakdown">
        {byType.length === 0 ? <Empty msg="No documents in the library yet. Add some in the Documents tab (P2)." /> : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={byType} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717a" }} angle={-20} textAnchor="end" interval={0} height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#71717a" }} />
              <Tooltip cursor={{ fill: "rgba(200,162,75,0.08)" }} />
              <Bar dataKey="count" fill={GOLD} radius={[4, 4, 0, 0]} maxBarSize={56} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Recently Added" testid="card-recent-docs">
          {(d.documents?.recent || []).length === 0 ? <Empty msg="Nothing added yet." /> : (
            <div className="space-y-2" data-testid="recent-docs-list">
              {d.documents.recent.map((r, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-2 last:border-0">
                  <div className="min-w-0">
                    <div className="text-zinc-900 truncate">{r.name}</div>
                    <div className="text-[11px] text-zinc-500">{r.type}</div>
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    r.status === "Finalized" ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card title="Top Tags" testid="card-tags">
          {(d.documents?.by_tag || []).length === 0 ? <Empty msg="No tags yet." /> : (
            <div className="flex flex-wrap gap-2">
              {d.documents.by_tag.map((t) => (
                <span key={t.name} className="inline-flex items-center gap-1 text-xs bg-zinc-100 text-zinc-700 rounded-full px-2.5 py-1">
                  {t.name}<span className="text-[10px] text-zinc-400">{t.count}</span>
                </span>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

const Empty = ({ msg }) => <div className="text-xs text-zinc-400 py-8 text-center">{msg}</div>;

const MiniPie = ({ data, colors, center, label }) => (
  <div className="relative">
    <ResponsiveContainer width="100%" height={150}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={42} outerRadius={62} paddingAngle={2}>
          {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      <span className="text-xl font-display font-extrabold text-zinc-900">{center}</span>
      <span className="text-[10px] text-zinc-500">{label}</span>
    </div>
  </div>
);
